/**
 * 信用卡分期内核（`DR-060` · 批 `104` · B 域 PRD 增补 §5.2.4）
 *
 * 依据：`DR-060`（口径定稿：D1 消费时点计支出 · D2 账单=现金流 · D3 负债只计未还本金 ·
 * D4 手续费逐期计支出可改 · D5 溯源）· `DR-026` D1（负债方第 4 类）· B 域 PRD 增补 §6/§7。
 *
 * ## 🔴 本文件是分期**算法的唯一实现处**（铁律 24）
 *
 * | 口径 | 唯一函数 | 谁消费 |
 * |---|---|---|
 * | 期次生成（最大余数法 + 逐月到期日） | {@link buildInstallmentPeriods} | 服务端建分期落库 · 端侧建分期预演 |
 * | 账单成对调整（− 移出本金 + 本期应还） | {@link installmentBillAdjustment} | 账户服务（当期 + 往期账单） |
 * | 卡面「分期未还」摘要 | {@link installmentCardSummary} | 账户服务（`CreditCardView` / 账单接口） |
 * | 手续费总额换算 | {@link installmentFeeTotal} | 服务端建分期 · 端侧预演 |
 *
 * 载体：🆕 `installment_plans` + `installment_plan_sources` + `loan_schedules.plan_id`
 * （**复用** `interest` 列存分期手续费 —— 贷款侧是利息、分期侧是手续费，同为"资金成本"）。
 * 🔴 **不复用 `schedules` 表**：`uq_schedules_account`（账户 : 规则 = 1:1），一卡多分期必撞键
 * （`DR-060` §4.1）；`nucleus/schedule.ts` 对 `PlanKind.Installment` 的拦截**保持不动**。
 */

import type { Cents } from '../types/brand.js';
import { addMonths } from './calendar.js';
import { dayInMonth } from './account.js';
import { allocate, sumCents, toCents } from './money.js';

// ───────────────────────────── 期次生成 ─────────────────────────────

/** 一条分期期次（建分期时的**生成结果**，落库即 `loan_schedules` 一行） */
export interface InstallmentPeriodSpec {
  /** 期次（从 0 起；与 `loan_schedules.period_index` 同义） */
  periodIndex: number;
  /** 到期日 `YYYY-MM-DD`（= 该期账单日） */
  dueDate: string;
  /** 本期本金（整数分；最大余数法平摊，Σ == 本金总额） */
  principal: Cents;
  /** 本期手续费（整数分；同法平摊，可逐期手改） */
  fee: Cents;
}

/** 期次生成的常量边界（B 域 PRD 增补 §6.1：`periods` 2–60） */
export const InstallmentPeriodsMin = 2;
export const InstallmentPeriodsMax = 60;

/**
 * 分期期次的**到期日序列** —— 与信用卡账单日**同一实现**（`dayInMonth` 逐月 + 短月 clamp）。
 *
 * 🔴 **锚点是"配置的账单日 `billingDay`"，不是 `startBillDay` 的日号**：
 * 账单日 31 的卡，首期 `2026-01-31` ⇒ 第二期必须是 `2026-02-28`（2 月末）、第三期 `2026-03-31`；
 * 若从已 clamp 的 `02-28` 继续加月，会衰减成 `03-28` —— 与 3 月账单日 `03-31` 错开，
 * 账单扣减就再也对不上期次（与 `computeCreditBill` 的 `windowStart` 教训同源）。
 */
export function installmentDueDates(startBillDay: string, billingDay: number, periods: number): string[] {
  const out: string[] = [];
  for (let n = 0; n < periods; n += 1) {
    out.push(dayInMonth(addMonths(startBillDay, n), billingDay));
  }
  return out;
}

/**
 * `startBillDay` 是否为该卡**有效的账单日**（B 域 PRD 增补 §6.4：必须是该卡的账单日）。
 *
 * 判据 = 它等于"该月按 `billingDay` clamp 后的账单日"（如账单日 31 的卡，`02-28` 合法、`02-27` 不合法）。
 */
export function isValidInstallmentStartBillDay(startBillDay: string, billingDay: number): boolean {
  return dayInMonth(startBillDay, billingDay) === startBillDay;
}

/**
 * 生成全部分期期次（**最大余数法**，Σ 每期本金 == 本金总额，逐分恒等 —— `AC-B2.11`）。
 *
 * - 本金与手续费分别用 `allocate(total, 全 1 权重)` 平摊（余数降序、索引升序，幂等可复算）；
 * - 到期日 = 首期账单日逐月 + n（短月 clamp，与信用卡账期同一实现）；
 * - 调用方负责 `periods` 越界 / `startBillDay` 形态的业务校验（`E4` / `E9`），本函数只做最后防线。
 *
 * @throws RangeError `periods` 越界或 `startBillDay` 不是合法账单日时（契约层与服务端已挡）
 */
export function buildInstallmentPeriods(input: {
  /** 本金总额（整数分，> 0） */
  principalTotal: Cents;
  /** 期数（2–60） */
  periods: number;
  /** 手续费总额（整数分，可为 0 = 免息） */
  feeTotal: Cents;
  /** 首期账单日 `YYYY-MM-DD`（必须为该卡的有效账单日） */
  startBillDay: string;
  /** 该卡的账单日（1–31）—— 到期日锚点 */
  billingDay: number;
}): InstallmentPeriodSpec[] {
  const { principalTotal, feeTotal, startBillDay, billingDay } = input;
  const periods = input.periods;
  if (!Number.isInteger(periods) || periods < InstallmentPeriodsMin || periods > InstallmentPeriodsMax) {
    throw new RangeError(`期数须在 ${InstallmentPeriodsMin}–${InstallmentPeriodsMax}：${periods}`);
  }
  if ((principalTotal as number) <= 0) throw new RangeError(`本金总额必须为正：${principalTotal}`);
  if ((feeTotal as number) < 0) throw new RangeError(`手续费总额不允许负数：${feeTotal}`);
  if (!isValidInstallmentStartBillDay(startBillDay, billingDay)) {
    throw new RangeError(`首期账单日 ${startBillDay} 不是该卡（账单日 ${billingDay}）的有效账单日`);
  }

  const ones = Array.from({ length: periods }, () => 1);
  const principals = allocate(principalTotal, ones);
  const fees = allocate(feeTotal, ones);
  const dueDates = installmentDueDates(startBillDay, billingDay, periods);

  return periods === 0
    ? []
    : Array.from({ length: periods }, (_, i) => ({
        periodIndex: i,
        dueDate: dueDates[i]!,
        principal: principals[i]!,
        fee: fees[i]!,
      }));
}

/**
 * 手续费总额换算（`DR-060` §4.4：可按"总手续费"自动平摊，也可按"每期"录入）。
 *
 * - `total`：直接使用（生成时再平摊到各期）；
 * - `per_period`：总额 = 每期 × 期数（每期手续费恒定）。
 */
export function installmentFeeTotal(mode: 'total' | 'per_period', feeValue: Cents, periods: number): Cents {
  if ((feeValue as number) < 0) throw new RangeError(`手续费不允许负数：${feeValue}`);
  return toCents(mode === 'total' ? (feeValue as number) : (feeValue as number) * periods);
}

// ───────────────────────────── 账单成对调整 ─────────────────────────────

/**
 * 分期对**某一期账单**的成对调整（喂给 `computeCreditBill` 的 `installment` 入参）。
 *
 * 账单公式（`DR-060` §4.3 · `AC-B2.14` / `AC-B2.15`）：
 * ```
 * 办理那一期：本期应还 = 窗口消费 − 分期本金 + 本期分期应还(本金 + 手续费)
 * 其后各期：  本期应还 = 窗口消费 + 本期分期应还
 * ```
 *
 * @param billDay   该期账单日
 * @param plans     该卡的分期计划（**调用方只需给 active 的**；`start_bill_day` / `principal_total`）
 * @param periods   该卡的分期期次（`due_date` / `principal` / `interest`（=手续费）/ `status`）
 * @returns 两项都为 0 时返回 `null`（调用方不出键 —— 可选键剔键纪律）
 */
export function installmentBillAdjustment(params: {
  billDay: string;
  plans: readonly { startBillDay: string; principalTotal: Cents }[];
  periods: readonly { dueDate: string; principal: Cents; interest: Cents; status: string }[];
}): { principalDeducted: Cents; dueThisPeriod: Cents } | null {
  let principalDeducted = 0;
  for (const p of params.plans) {
    if (p.startBillDay === params.billDay) principalDeducted += p.principalTotal as number;
  }
  let dueThisPeriod = 0;
  for (const s of params.periods) {
    // 🔴 只数 `pending`：已还期次的钱已实际流过，再进 due 会误导用户重复还款
    if (s.dueDate === params.billDay && s.status === 'pending') {
      dueThisPeriod += (s.principal as number) + (s.interest as number);
    }
  }
  if (principalDeducted === 0 && dueThisPeriod === 0) return null;
  return { principalDeducted: toCents(principalDeducted), dueThisPeriod: toCents(dueThisPeriod) };
}

// ───────────────────────────── 卡面摘要 / 负债 ─────────────────────────────

/**
 * 卡面「分期未还」摘要（B 域 PRD 增补 §4.2 的第三块）。
 *
 * 🔴 `remainingPrincipal` **只含本金**（`DR-060` D3：手续费不是负债）；
 * 全部期次已还（无 pending）⇒ 返回 `null`（卡面隐藏该块，`AC-B2.19`）。
 */
export interface InstallmentCardSummary {
  /** 分期未还本金 = Σ pending 期次本金（整数分；**不含手续费**） */
  remainingPrincipal: Cents;
  /** 剩余期数（pending 计数） */
  remainingPeriods: number;
  /** 预计结清账单日 = 全部期次里最晚的 `due_date`（`YYYY-MM-DD`；展示取年月） */
  estimatedEndBill: string | null;
}

/**
 * 汇总一张卡的分期摘要（`plan_id` 维度）。
 *
 * @param periods 该卡**全部 active 计划**的期次（调用方过滤掉 settled / cancelled）
 * @returns 没有 pending 期次 ⇒ `null`（卡面不显示「分期未还」）
 */
export function installmentCardSummary(
  periods: readonly { dueDate: string; principal: Cents; status: string }[],
): InstallmentCardSummary | null {
  let remainingPrincipal = 0;
  let remainingPeriods = 0;
  let endBill: string | null = null;
  for (const s of periods) {
    if (s.status === 'pending') {
      remainingPrincipal += s.principal as number;
      remainingPeriods += 1;
    }
    if (endBill === null || s.dueDate > endBill) endBill = s.dueDate;
  }
  if (remainingPeriods === 0) return null;
  return {
    remainingPrincipal: toCents(remainingPrincipal),
    remainingPeriods,
    estimatedEndBill: endBill,
  };
}

/**
 * 分期负债桶（`installmentPending`）的取数口径 —— **Σ pending 期次本金，按计划分组**。
 *
 * `composeNetWorth` 的 `installmentPendingPrincipal` 每项 = 一个计划的未还本金
 * （保留计划粒度，净资产明细里能看到每一笔）；`credit` 桶的成对剥离用**按账户合计**
 * （`DR-060` §3：`credit = min(0, 卡余额 + Σ pending 本金)`），两个口径同源同实现。
 */
export function installmentPendingOfPeriods(
  periods: readonly { principal: Cents; status: string }[],
): Cents {
  return sumCents(periods.filter((s) => s.status === 'pending').map((s) => s.principal));
}
